<template>
  <div class="navbar-wrapper">
    <el-menu class="navbar" mode="horizontal">
      <hamburger class="hamburger-container" :toggleClick="toggleSideBar" :isActive="sidebar.opened"></hamburger>

      <el-dropdown class="avatar-container" trigger="click">
        <!-- ===== 修改 START：在头像左侧新增商家名称（随头像一同固定于导航栏右侧） ===== -->
        <!-- 视觉排列：[商家名称] [头像▾] -->
        <div class="avatar-content">
          <!-- 商家名称徽章：位于头像左侧，样式参照拼多多商家后台 -->
          <div
            class="merchant-name-badge"
            v-if="merchantDisplayName"
            :title="merchantDisplayName"
          >
            <i class="el-icon-s-shop merchant-badge-icon"></i>
            <span class="merchant-badge-text">{{ merchantDisplayName }}</span>
          </div>
          <!-- 头像 -->
          <div class="avatar-wrapper">
            <img class="user-avatar" :src="avatar || defaultAvatar">
            <i class="el-icon-caret-bottom"></i>
          </div>
        </div>
        <!-- ===== 修改 END ===== -->
        <el-dropdown-menu class="user-dropdown" slot="dropdown">
          <router-link class="inlineBlock" to="/">
            <el-dropdown-item>
              首页
            </el-dropdown-item>
          </router-link>
          <el-dropdown-item divided>
            <span @click="handleOpenAvatarDialog" style="display:block;">修改头像</span>
          </el-dropdown-item>
          <el-dropdown-item divided>
            <span @click="logout" style="display:block;">退出</span>
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>

      <breadcrumb></breadcrumb>
    </el-menu>

    <!-- 修改头像弹窗 -->
    <el-dialog
      title="修改头像"
      :visible.sync="avatarDialogVisible"
      :append-to-body="true"
      width="420px">
      <el-form :model="userForm" label-width="90px" size="small">
        <el-form-item label="当前头像：">
          <img :src="avatar || defaultAvatar" class="preview-avatar">
        </el-form-item>
        <el-form-item label="上传新头像：">
          <single-upload v-model="userForm.icon"></single-upload>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="avatarDialogVisible = false" size="small">取 消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="handleConfirmAvatar" size="small">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Breadcrumb from '@/components/Breadcrumb'
import Hamburger from '@/components/Hamburger'
import SingleUpload from '@/components/Upload/singleUpload'
import { updateAdmin } from '@/api/login'
// ===== 新增：导入商家选项接口与 Cookie 工具（用于加载商家名称） =====
import { fetchListWithMerchantOptionChildren } from '@/api/productMerchantOption'
import { getCookie } from '@/utils/support'

export default {
  name: 'Navbar',
  components: {
    Breadcrumb,
    Hamburger,
    SingleUpload
  },
  data() {
    return {
      // 头像为空时的兜底图（项目内置固定图片）
      defaultAvatar: require('@/assets/images/nullHead.png'),
      avatarDialogVisible: false,
      submitLoading: false,
      userForm: {
        id: null,
        icon: ''
      },
      // ===== 新增：商家信息相关（用于在头像左侧展示商家名称） =====
      merchantOptions: [],       // 商家选项（含 merchantId / name）
      merchantInfoMap: {},       // 商家信息映射（merchantId -> { name, merchantType }）
      currentMerchantId: null,   // 当前商家ID（管理员为 null）
      merchantDisplayName: ''    // 顶部展示用的商家名称
    }
  },
  computed: {
    ...mapGetters([
      'sidebar',
      'avatar',
      'name'
    ]),
    // 当前登录用户 id（请根据你实际的 user store 结构调整取值路径）
    userId() {
      const user = this.$store.state.user
      return user && user.id ? user.id : null
    }
  },
  // ===== 新增：组件创建时加载商家信息 =====
  created() {
    this.loadMerchantInfo()
  },
  methods: {
    toggleSideBar() {
      this.$store.dispatch('ToggleSideBar')
    },
    logout() {
      this.$store.dispatch('LogOut').then(() => {
        location.reload() // 为了重新实例化vue-router对象 避免bug
      })
    },
    // 打开头像修改弹窗，回填当前用户信息
    handleOpenAvatarDialog() {
      this.userForm = {
        id: this.userId,
        icon: this.avatar
      }
      this.avatarDialogVisible = true
    },
    // 确认修改：调用和用户管理页相同的 updateAdmin 接口
    handleConfirmAvatar() {
      if (!this.userForm.icon) {
        this.$message({
          type: 'warning',
          message: '请先上传头像'
        })
        return
      }
      if (!this.userForm.id) {
        this.$message({
          type: 'error',
          message: '未获取到当前用户信息，请刷新页面后重试'
        })
        return
      }
      this.submitLoading = true
      updateAdmin(this.userForm.id, {
        id: this.userForm.id,
        icon: this.userForm.icon
      }).then(() => {
        this.submitLoading = false
        // 同步更新 Vuex 中的头像，Navbar 头像立即刷新
        this.$store.commit('SET_AVATAR', this.userForm.icon)
        this.avatarDialogVisible = false
        this.$message({
          type: 'success',
          message: '头像修改成功！'
        })
      }).catch(() => {
        this.submitLoading = false
      })
    },

    // ===== 新增：加载商家信息 =====
    /**
     * 加载商家信息，用于在 Navbar 头像左侧显示商家名称
     * - 管理员（username === 'admin'）：显示"平台管理员"
     * - 商家用户：从 localStorage / cookie 获取 merchantId，匹配商家名称
     * 说明：商家数据来源与 ProductInfoDetail.vue 中的选择商品商家功能保持一致
     *      （同样调用 fetchListWithMerchantOptionChildren 接口）
     */
    async loadMerchantInfo() {
      try {
        // 1. 判断当前用户是否为管理员（与 ProductInfoDetail.vue 保持一致）
        const username = getCookie('username')
        let userInfo = null
        const storedUser = localStorage.getItem('userInfo') || localStorage.getItem('user')
        if (storedUser) {
          try {
            userInfo = JSON.parse(storedUser)
          } catch (e) {
            console.error('解析用户信息失败:', e)
          }
        }
        let isAdmin = false
        if (username === 'admin' || (userInfo && userInfo.username === 'admin')) {
          isAdmin = true
        }

        // 2. 获取商家列表（与 ProductInfoDetail.vue 的 getMerchantOptions 逻辑一致）
        const res = await fetchListWithMerchantOptionChildren()
        const list = res.data || []
        this.merchantOptions = []
        this.merchantInfoMap = {}
        for (let i = 0; i < list.length; i++) {
          const item = list[i]
          const showtip = item.name + '(' + item.merchantId + ')'
          this.merchantOptions.push({ merchantId: item.merchantId, name: showtip })
          this.merchantInfoMap[item.merchantId] = {
            name: item.name,
            merchantType: item.merchantType || ''
          }
        }

        if (isAdmin) {
          // 管理员：无绑定商家，显示"平台管理员"
          this.currentMerchantId = null
          this.merchantDisplayName = '平台管理员'
          return
        }

        // 3. 商家用户：解析 merchantId（与 ProductInfoDetail.vue 一致）
        let merchantId = null
        if (userInfo) {
          if (userInfo.memberId) {
            merchantId = userInfo.memberId
          } else if (userInfo.merchantId) {
            merchantId = userInfo.merchantId
          }
        }
        if (!merchantId) {
          const cookieMerchantId = getCookie('merchantId')
          if (cookieMerchantId) merchantId = parseInt(cookieMerchantId)
        }

        if (merchantId) {
          this.currentMerchantId = merchantId
          // 优先从 merchantInfoMap 取，取不到再从 merchantOptions 兜底
          const info = this.merchantInfoMap[merchantId]
          if (info && info.name) {
            this.merchantDisplayName = info.name
          } else {
            const found = this.merchantOptions.find(opt => opt.merchantId === merchantId)
            this.merchantDisplayName = found ? found.name : ''
          }
        } else {
          // 兜底：如果既不是管理员也拿不到 merchantId，则不显示
          this.merchantDisplayName = ''
        }
      } catch (error) {
        // 商家信息属于附加展示，失败不阻塞页面其它功能
        console.warn('加载商家信息失败（不影响页面其它功能）：', error)
        this.merchantDisplayName = ''
      }
    }
  }
}
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
.navbar {
  height: 50px;
  line-height: 50px;
  border-radius: 0px !important;
  .hamburger-container {
    line-height: 58px;
    height: 50px;
    float: left;
    padding: 0 10px;
  }
  .screenfull {
    position: absolute;
    right: 90px;
    top: 16px;
    color: red;
  }
  .avatar-container {
    height: 50px;
    display: inline-block;
    position: absolute;
    right: 35px;

    /* ===== 新增：头像与商家名称的横向容器（[商家名称] [头像]） ===== */
    .avatar-content {
      display: flex;
      align-items: center;
      gap: 12px;
      height: 50px;
    }

    /* ===== 新增：商家名称徽章（拼多多商家后台风格，静态不浮动） ===== */
    .merchant-name-badge {
      display: flex;
      align-items: center;
      gap: 4px;
      height: 32px;
      padding: 0 12px;
      /* 白底 + 极浅灰边框 */
      background: #ffffff;
      border: 1px solid #e6e8eb;
      border-radius: 6px;
      /* 极克制的阴影，避免浮动感 */
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
      /* 字体风格：深灰、加粗适中，贴近拼多多商务感 */
      font-size: 13px;
      font-weight: 500;
      color: #333333;
      letter-spacing: 0.2px;
      max-width: 160px;
      cursor: default;
      /* 关键：固定不动，无任何过渡与浮动动画 */
      transition: none;
      transform: none;

      /* 图标：拼多多风格使用暖色（橙红系），象征店铺 */
      .merchant-badge-icon {
        color: #ff6a00;
        font-size: 14px;
        flex-shrink: 0;
      }

      /* 商家名称文字：溢出用省略号，单行显示 */
      .merchant-badge-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 120px;
        line-height: 1;
      }
    }

    /* ===== 原有：头像包装（保留原结构，去掉 margin-top，改由 flex 垂直居中） ===== */
    .avatar-wrapper {
      cursor: pointer;
      position: relative;
      height: 40px;
      width: 40px;
      // margin-top: 5px; /* 已由 .avatar-content 的 align-items: center 接管垂直居中 */
      .user-avatar {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: block;
      }
      .el-icon-caret-bottom {
        position: absolute;
        right: -20px;
        top: 25px;
        font-size: 12px;
        font-family: element-icons!important;
      }
    }
  }
}
.preview-avatar {
  width: 80px;
  height: 80px;
  border-radius: 10px;
}
</style>


// WEBPACK FOOTER //
// src/views/layout/components/Navbar.vue